import React from 'react';
import { Building2, Layers, Sparkles, BadgeCheck, Handshake, RefreshCw, XCircle } from 'lucide-react';
import { Reveal } from '../ui/Reveal';
interface HowMarketplaceWorksProps {
  /** Tailors the audience-facing language in the closing line. */
  audience?: 'brands' | 'marketing agencies' | 'you';
  /** Background treatment so the section sits well on different pages. */
  background?: 'white' | 'muted';
}
const STEPS = [{
  icon: BadgeCheck,
  title: 'We vet every agency',
  body: 'Before an agency can create an account, our team meets with them one-on-one to verify who they are and the talent they represent — and we check in regularly to keep them accountable.'
}, {
  icon: Building2,
  title: 'Agencies manage their own rosters',
  body: 'Each vetted agency maintains its own live roster of creators — updating talent, performance data, pricing, and availability directly. No stale PDFs, no guesswork.'
}, {
  icon: Layers,
  title: 'We pool it into one network',
  body: 'Zano brings every agency roster together into one searchable database — so you can discover thousands of represented creators across hundreds of trusted agencies in one place.'
}];
export const HowMarketplaceWorks = ({
  audience = 'brands',
  background = 'muted'
}: HowMarketplaceWorksProps) => {
  const audienceLabel = audience === 'you' ? 'your agency clients' : audience;
  return <section className={`py-20 md:py-24 ${background === 'muted' ? 'bg-gray-50 border-y border-gray-100' : 'bg-white'}`} data-id="element-1116">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1117">
        <Reveal data-id="element-1118">
          <div className="max-w-3xl mx-auto text-center mb-14" data-id="element-1119">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zano-blush text-zano-pink text-xs font-semibold border border-zano-pink/20 mb-5" data-id="element-1120">
              <Sparkles size={13} data-id="element-1121" /> How the marketplace works
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-zano-charcoal mb-4 leading-tight" data-id="element-1122">
              Vetted agency accounts — not scraped from the internet
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1123">
              Anyone can scrape the internet for creator data. We don't. Zano is
              built on real relationships with high-quality talent agencies who
              manage their own rosters — and we make it easier for them to get
              booked at scale.
            </p>
          </div>
        </Reveal>

        {/* The 3-step flow */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 relative" data-id="element-1124">
          {STEPS.map((step, idx) => <Reveal key={step.title} delay={idx * 0.1} className="h-full" data-id="element-1125">
              <div className="relative h-full bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-id="element-1126">
                <div className="flex items-center gap-3 mb-4" data-id="element-1127">
                  <div className="w-11 h-11 rounded-xl bg-zano-lavender/60 border border-zano-pink/10 flex items-center justify-center shrink-0" data-id="element-1128">
                    <step.icon size={20} className="text-zano-pink" data-id="element-1129" />
                  </div>
                  <span className="text-xs font-bold text-gray-400" data-id="element-1130">
                    Step {idx + 1}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1131">
                  {step.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1132">
                  {step.body}
                </p>
              </div>
            </Reveal>)}
        </div>

        {/* Scraped vs. Zano contrast */}
        <Reveal delay={0.2} data-id="element-1133">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto" data-id="element-1134">
            <div className="bg-white rounded-2xl p-6 border border-gray-200" data-id="element-1135">
              <div className="flex items-center gap-2 mb-3" data-id="element-1136">
                <XCircle size={18} className="text-gray-400 shrink-0" data-id="element-1137" />
                <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider" data-id="element-1138">
                  Scraped databases
                </h4>
              </div>
              <ul className="space-y-2.5 text-sm text-gray-500" data-id="element-1139">
                <li data-id="element-1140">Guessed emails and dead contact info</li>
                <li data-id="element-1141">Outdated follower counts and no real performance data</li>
                <li data-id="element-1142">No one accountable for the creator or the deal</li>
                <li data-id="element-1143">Cold outreach into a void</li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-6 border-2 border-zano-pink/30 shadow-float" data-id="element-1144">
              <div className="flex items-center gap-2 mb-3" data-id="element-1145">
                <BadgeCheck size={18} className="text-zano-pink shrink-0" data-id="element-1146" />
                <h4 className="text-sm font-bold text-zano-pink uppercase tracking-wider" data-id="element-1147">
                  The Zano network
                </h4>
              </div>
              <ul className="space-y-2.5 text-sm text-gray-700" data-id="element-1148">
                <li className="flex items-start gap-2" data-id="element-1149">
                  <Handshake size={16} className="text-zano-pink shrink-0 mt-0.5" data-id="element-1150" />
                  Verified agencies we've personally met and vetted
                </li>
                <li className="flex items-start gap-2" data-id="element-1151">
                  <RefreshCw size={16} className="text-zano-pink shrink-0 mt-0.5" data-id="element-1152" />
                  Live rosters maintained by the managers themselves
                </li>
                <li className="flex items-start gap-2" data-id="element-1153">
                  <Building2 size={16} className="text-zano-pink shrink-0 mt-0.5" data-id="element-1154" />
                  Direct lines to the people who represent the talent
                </li>
                <li className="flex items-start gap-2" data-id="element-1155">
                  <BadgeCheck size={16} className="text-zano-pink shrink-0 mt-0.5" data-id="element-1156" />
                  Relationships built to book talent at scale
                </li>
              </ul>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.3} data-id="element-1157">
          <p className="text-center text-base text-gray-600 max-w-2xl mx-auto mt-10" data-id="element-1158">
            The agencies manage the talent. We make it easier for them to get
            booked — and pool it all together so{' '}
            <span className="font-semibold text-zano-charcoal" data-id="element-1159">
              {audienceLabel}
            </span>{' '}
            can discover high-quality, represented creators in one trusted
            place.
          </p>
        </Reveal>
      </div>
    </section>;
};